type RiskNode = {
  Id: string;
  Tier: number;
  Title: string;
  ParentRiskId?: string | null;
};

/**
 * Resolves the T1 (root) ancestor of a risk in the hierarchy.
 *
 * Walks up the ParentRiskId chain from the given risk until a Tier 1
 * ancestor is found. Returns null if the chain is broken (orphaned) or
 * if a cycle is detected (max-depth guard). A Tier 1 risk is its own
 * ancestor.
 *
 * This is the single client-side walk — `getT1CategoryTitle` and the CUS-242
 * conditional-form source both read it, so the two cannot drift. It mirrors
 * the `risksmart.risk_t1_category` recursive CTE, which serves the same lookup
 * server-side for CDS.
 */
export const findT1Ancestor = <T extends RiskNode>(
  risk: T,
  riskById: Map<string, T>
): null | T => {
  if (risk.Tier === 1) {
    return risk;
  }
  let current: T | undefined = risk;
  let depth = 0;
  while (current?.ParentRiskId && depth < 10) {
    current = riskById.get(current.ParentRiskId);
    if (current?.Tier === 1) {
      return current;
    }
    depth++;
  }

  return null;
};

/**
 * Resolves the T1 (root) ancestor title for a risk in the hierarchy.
 *
 * Returns null if the chain is broken (orphaned) or if a cycle is detected.
 */
export const getT1CategoryTitle = (
  risk: RiskNode,
  riskById: Map<string, RiskNode>
): null | string => findT1Ancestor(risk, riskById)?.Title ?? null;
